/**
 * Calculate recommended indoor/outdoor seating capacity
 * based on business type, square footage, and seating preference.
 */

interface SeatingCapacity {
  indoorSeats: number;
  outdoorSeats: number;
  totalSeats: number;
  sqftPerIndoorSeat: number;
  sqftPerOutdoorSeat: number;
  note: string;
}

// Industry-standard square feet per seat by business type
const SQFT_PER_SEAT_INDOOR: Record<string, number> = {
  "restaurant": 18,
  "brunch-spot": 18,
  "fast-casual": 14,
  "pizzeria": 16,
  "coffee-shop": 15,
  "bakery": 20,
  "bakery-cafe": 16,
  "bar": 20,
  "wine-shop": 16,
  "ice-cream-shop": 14,
  "juice-bar": 14,
  "hotel": 22,
};

// Outdoor seating is typically more spacious
const SQFT_PER_SEAT_OUTDOOR: Record<string, number> = {
  "restaurant": 22,
  "brunch-spot": 22,
  "fast-casual": 16,
  "pizzeria": 20,
  "coffee-shop": 18,
  "bakery": 24,
  "bakery-cafe": 18,
  "bar": 24,
  "wine-shop": 20,
  "ice-cream-shop": 16,
  "juice-bar": 16,
  "hotel": 26,
};

// What percentage of total sqft is usable as dining area (rest is kitchen, storage, restrooms, etc.)
const DINING_RATIO: Record<string, number> = {
  "restaurant": 0.55,
  "brunch-spot": 0.55,
  "fast-casual": 0.60,
  "pizzeria": 0.50,
  "coffee-shop": 0.65,
  "bakery": 0.40,
  "bakery-cafe": 0.55,
  "bar": 0.65,
  "wine-shop": 0.60,
  "ice-cream-shop": 0.70,
  "juice-bar": 0.65,
  "hotel": 0.50,
};

export function calculateSeatingCapacity(
  businessType: string,
  totalSqft: number,
  seatingType: "indoor" | "outdoor" | "both",
  outdoorSqft?: number
): SeatingCapacity {
  const sqftIndoor = SQFT_PER_SEAT_INDOOR[businessType] || 18;
  const sqftOutdoor = SQFT_PER_SEAT_OUTDOOR[businessType] || 22;
  const diningRatio = DINING_RATIO[businessType] || 0.55;

  const usableSqft = totalSqft * diningRatio;

  let indoorSeats = 0;
  let outdoorSeats = 0;
  let note = "";

  if (seatingType === "indoor") {
    indoorSeats = Math.floor(usableSqft / sqftIndoor);
    note = `All ${indoorSeats} seats indoors. ${Math.round(diningRatio * 100)}% of your ${totalSqft.toLocaleString()} sq ft is usable as dining area (~${Math.round(usableSqft).toLocaleString()} sq ft).`;
  } else if (seatingType === "outdoor") {
    // Outdoor-only uses full space as outdoor
    outdoorSeats = Math.floor(usableSqft / sqftOutdoor);
    note = `All ${outdoorSeats} seats outdoors. Patio/terrace layout at ${sqftOutdoor} sq ft per seat.`;
  } else {
    // Both: split based on outdoor sqft if provided, otherwise 70/30 default
    const outdoorArea = outdoorSqft || Math.round(usableSqft * 0.30);
    const indoorArea = usableSqft - (outdoorSqft ? 0 : outdoorArea);
    const actualIndoorArea = outdoorSqft ? usableSqft : indoorArea;

    indoorSeats = Math.floor(actualIndoorArea / sqftIndoor);
    outdoorSeats = Math.floor(outdoorArea / sqftOutdoor);
    note = `${indoorSeats} indoor seats (${sqftIndoor} sq ft each) + ${outdoorSeats} outdoor seats (${sqftOutdoor} sq ft each). Based on ${Math.round(diningRatio * 100)}% dining area ratio.`;
  }

  return {
    indoorSeats,
    outdoorSeats,
    totalSeats: indoorSeats + outdoorSeats,
    sqftPerIndoorSeat: sqftIndoor,
    sqftPerOutdoorSeat: sqftOutdoor,
    note,
  };
}

export function getSeatingLabel(businessType: string): string {
  const labels: Record<string, string> = {
    "hotel": "Rooms",
    "fitness-studio": "Stations",
    "beauty-salon": "Chairs",
    "barber-shop": "Chairs",
    "nail-salon": "Stations",
  };
  return labels[businessType] || "Seats";
}
